import { useMemo } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Circle,
  Settings,
  Users,
  CreditCard,
  UserCheck,
  Plane,
  Sparkles,
  X,
  ArrowRight,
} from "lucide-react";

/**
 * Getting Started checklist card for the dashboard.
 *
 * Steps auto-detect completion from the user's real data where possible:
 *   - Preferences: persisted `onboarding_prefs_done` flag on the user
 *   - Traveler profile: user-settings `selected_traveler_id`
 *   - Travelers: travelers.length > 0
 *   - Payment cards: paymentMethodsCount > 0
 *   - First trip: trips.length > 0
 *
 * Dismissible via the X (persists `getting_started_hidden`). A toggle in
 * Settings turns it back on.
 */
export const GettingStartedCard = ({
  travelers = [],
  trips = [],
  paymentMethodsCount = 0,
  prefsDone = false,
  selectedTravelerId = null,
  onOpenSettings,
  onStartTrip,
  onDismiss,
  navigate,
}) => {
  const steps = useMemo(
    () => [
      {
        key: "preferences",
        label: "Set your preferences",
        description: "Currency, units, time & date format, and theme.",
        icon: Settings,
        done: !!prefsDone,
        cta: "Open Settings",
        action: () => onOpenSettings?.(),
      },
      {
        key: "traveler_profile",
        label: "Choose your traveler profile",
        description: 'Tell us which traveler is "you".',
        icon: UserCheck,
        done: !!selectedTravelerId,
        cta: "Open Settings",
        action: () => onOpenSettings?.(),
      },
      {
        key: "travelers",
        label: "Add your travelers",
        description: "The people you travel with, so trips can be assigned.",
        icon: Users,
        done: travelers.length > 0,
        cta: "Add Travelers",
        action: () => navigate?.("/travelers"),
      },
      {
        key: "cards",
        label: "Add your payment cards",
        description: "Track spending and match payments to the right card.",
        icon: CreditCard,
        done: paymentMethodsCount > 0,
        cta: "Add Cards",
        action: () => navigate?.("/payment-methods"),
      },
      {
        key: "first_trip",
        label: "Import or create your first trip",
        description: "Forward a confirmation email or add a trip manually.",
        icon: Plane,
        done: trips.length > 0,
        cta: "Start a Trip",
        action: () => onStartTrip?.(),
      },
    ],
    [
      prefsDone,
      selectedTravelerId,
      travelers.length,
      paymentMethodsCount,
      trips.length,
      onOpenSettings,
      onStartTrip,
      navigate,
    ]
  );

  const completedCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const allDone = completedCount === total;
  const pct = Math.round((completedCount / total) * 100);

  // Circular progress ring geometry
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (pct / 100) * circumference;

  return (
    <Card
      data-testid="getting-started-card"
      className="mb-6 sm:mb-8 overflow-hidden border-2 border-sky-200 dark:border-sky-800 bg-gradient-to-br from-white to-sky-50 dark:from-gray-900 dark:to-sky-950/40 shadow-lg"
    >
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Progress ring */}
            <div className="relative flex-shrink-0" data-testid="getting-started-progress">
              <svg width="64" height="64" className="-rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r={radius}
                  fill="none"
                  strokeWidth="6"
                  className="stroke-sky-100 dark:stroke-sky-900"
                />
                <circle
                  cx="32"
                  cy="32"
                  r={radius}
                  fill="none"
                  strokeWidth="6"
                  strokeLinecap="round"
                  className="stroke-sky-500 dark:stroke-sky-400"
                  style={{
                    strokeDasharray: circumference,
                    strokeDashoffset: dashOffset,
                    transition: "stroke-dashoffset 0.6s ease",
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                {allDone ? (
                  <Sparkles className="h-6 w-6 text-sky-500 dark:text-sky-400" />
                ) : (
                  <span className="text-sm font-bold text-sky-700 dark:text-sky-300">
                    {completedCount}/{total}
                  </span>
                )}
              </div>
            </div>

            <div>
              <h2
                className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: "Work Sans, sans-serif" }}
              >
                {allDone ? "You're all set! 🎉" : "Getting Started"}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {allDone
                  ? "You've completed every step. Happy travels!"
                  : `Complete these steps to get the most out of Travel Gadjet.`}
              </p>
            </div>
          </div>

          <button
            data-testid="getting-started-dismiss"
            onClick={onDismiss}
            title="Hide Getting Started (turn back on in Settings)"
            className="flex-shrink-0 rounded-full p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <ul className="space-y-2">
          {steps.map((step, idx) => {
            const StepIcon = step.icon;
            return (
              <li
                key={step.key}
                data-testid={`getting-started-step-${step.key}`}
                data-done={step.done}
                className={`flex items-center gap-3 sm:gap-4 rounded-xl border p-3 sm:p-4 transition-colors ${
                  step.done
                    ? "border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/20"
                    : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-sky-300 dark:hover:border-sky-700"
                }`}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                {/* Status circle */}
                <div className="flex-shrink-0">
                  {step.done ? (
                    <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                  ) : (
                    <Circle className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                  )}
                </div>

                {/* Icon badge */}
                <div
                  className={`flex-shrink-0 hidden sm:flex h-10 w-10 rounded-full items-center justify-center ${
                    step.done
                      ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400"
                      : "bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400"
                  }`}
                >
                  <StepIcon className="h-5 w-5" />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm sm:text-base font-semibold ${
                      step.done
                        ? "text-gray-500 dark:text-gray-500 line-through decoration-emerald-400/70"
                        : "text-gray-900 dark:text-gray-100"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                    {step.description}
                  </p>
                </div>

                {/* Action */}
                <div className="flex-shrink-0">
                  {step.done ? (
                    <span
                      className="text-xs font-medium text-emerald-600 dark:text-emerald-400 hidden sm:inline"
                      data-testid={`getting-started-done-${step.key}`}
                    >
                      Done
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`getting-started-cta-${step.key}`}
                      onClick={step.action}
                      className="rounded-full text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-700 hover:bg-sky-50 dark:hover:bg-sky-950"
                    >
                      <span className="hidden sm:inline">{step.cta}</span>
                      <ArrowRight className="h-4 w-4 sm:ml-1" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {allDone && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              data-testid="getting-started-hide-btn"
              onClick={onDismiss}
              className="rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Hide this
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GettingStartedCard;
